// A product's settings, /admin/products/<slug>. No board draws it (the map's recommendation, the
// critic's batch): what members see on the tile, the questions line, the terms line, the minimum the
// terms promise, who sees live sessions, The Funding Engine's WhatsApp link (C12) and the certificate
// prefix; the cover photo; a free item's card words and address; the look while it's a draft; for
// the masterclass its recording's line and the offer and the owner's card (owner only, read only for
// an admin). The mode, the access email switch and whether it has moved in are shown, not changed:
// they're switched at each product's move with Adam's yes. A product made in admin (Add a product or
// Duplicate) is the exception: an owner makes its Stripe purchases live here, once (the product-types
// plan, 13 Oct). A mastermind made in admin has its membership length set here, as its term; The Property
// Accelerator's 12 months came with its move and stay as they are. Every paid product's terms link (its
// settings.terms_url, which a copy keeps) is shown and changed with its terms, and every mastermind's
// guarantee says here whether members see the on track pill, never the guarantee or its rules (the product-types
// follow-ups, 13 Oct; Adam, 18 Oct). Owner and admin (the shell).
import { useEffect, useRef, useState } from 'react'
import { PageHeader, Card, Button, ButtonLink, Field, TextInput, TextArea, Select, Options, Switch, Loading, Problem, useLoad, adminPath, InfoNote, ConfirmPanel } from '../ui/index.js'
import { Link, scrollToHash } from '../../lib/router.jsx'
import { duration } from '../../lib/format.js'
import { SETTINGS, LOOK_NAMES, ADD } from '../products/copy.js'
import { typeLabel, isMovedIn, outsideHost, isEmail, isHttps, contrastCheck, normalHex, isHex, fontFor, looksFor, madeInAdmin, paidMonthly } from '../products/model.js'
import { loadProductSettings, updateProduct, saveWhatsapp, uploadCover, setPurchasesLive } from '../products/data.js'
import { LookPicker } from '../products/LookPicker.jsx'
import { OfferEditor } from '../products/OfferEditor.jsx'
import { Cover } from '../products/bits.jsx'
import '../../styles/admin-products.css'

const MONTHS = [1, 3, 6, 12, 18, 24]
// a mastermind's membership length, 1 to 60 months (catalogue.term_months): always a length, since its
// pages count the months of the term (Month 3 of 12)
const TERM_MONTHS = Array.from({ length: 60 }, (_, i) => i + 1)
// the terms line Add a product gives a membership, which follows its length while it's unchanged
const termsLineFor = months => `${months}-month membership.`

export default function Product({ api, route, role }) {
  const slug = route.params.slug
  const page = useLoad(() => loadProductSettings(api, slug), [api, slug])
  const d = page.data
  const p = d?.product
  // "Recording and offer" opens this page at the offer, once it has drawn
  const ready = page.status === 'ready'
  useEffect(() => { if (ready && window.location.hash) scrollToHash() }, [ready])

  const head = (title, crumb) => (
    <>
      <PageHeader eyebrow={<HeadCrumbs name={crumb} />} title={title} docTitle={crumb ? `Admin: ${crumb} settings` : undefined} />
    </>
  )
  if (page.status === 'loading') return <div className="ap-page">{head(SETTINGS.title)}<Loading /></div>
  if (page.status === 'error') return <div className="ap-page">{head(SETTINGS.title)}<Problem error={page.error} onRetry={page.reload} /></div>
  if (!p) {
    return (
      <div className="ap-page">
        {head(SETTINGS.notFoundTitle)}
        <Card><p className="ad-lede">{SETTINGS.notFound}</p><p><ButtonLink to={adminPath.products()} size="sm">{SETTINGS.back}</ButtonLink></p></Card>
      </div>
    )
  }
  const patch = next => page.setData(x => ({ ...x, ...next, product: { ...x.product, ...(next.product || {}) } }))
  const moved = isMovedIn(p)
  const paid = p.access_kind === 'paid'
  // keyed by the product, so moving from 1 product's settings to another's starts every card afresh
  // (the page keeps the last product's rows while the next 1's load)
  return (
    <div className="ap-page ap-settings" key={p.id}>
      {head(SETTINGS.title, p.name)}
      {!moved && (
        <Card title={SETTINGS.notMovedH2} id="ap-not-moved">
          <p className="ad-lede">{SETTINGS.notMovedLine(p.name, outsideHost(p))}</p>
        </Card>
      )}
      <About api={api} p={p} onSaved={row => patch({ product: row })} editable={moved} />
      {moved && paid && <Terms api={api} p={p} extras={d.extras} onSaved={next => patch(next)} />}
      {moved && p.type === 'mastermind' && <Guarantee api={api} p={p} onSaved={row => patch({ product: row })} />}
      {moved && !paid && <FreeCardWords api={api} p={p} onSaved={row => patch({ product: row })} />}
      {moved && <LookCard api={api} p={p} onSaved={row => patch({ product: row })} />}
      {moved && p.type === 'masterclass' && <RecordingLine d={d} />}
      {moved && p.type === 'masterclass' && (
        <OfferEditor api={api} product={p} offer={d.offer} products={d.others} owner={role === 'owner'}
          onSaved={(offer, sells) => patch({ offer, product: { offer_product_id: sells } })} />
      )}
      {paid && <Runs api={api} p={p} billing={d.billing} owner={role === 'owner'} onBilling={billing => patch({ billing })} />}
    </div>
  )
}

function HeadCrumbs({ name }) {
  return (
    <span className="ap-eyebrow-crumbs">
      <Link to={adminPath.products()}>{SETTINGS.crumbs}</Link>
      {name && <><span aria-hidden="true">/</span><span>{name}</span></>}
    </span>
  )
}

// What a card's Save changes says: Saved., or the refusal's words
function useSave() {
  const [state, setState] = useState({ busy: false, answer: null })
  const run = async fn => {
    setState({ busy: true, answer: null })
    try {
      await fn()
      setState({ busy: false, answer: { text: SETTINGS.saved } })
    } catch (e) {
      setState({ busy: false, answer: { text: e.message, problem: true } })
    }
  }
  return { ...state, run, clear: () => setState(s => (s.answer ? { ...s, answer: null } : s)) }
}

function SaveRow({ save, label = SETTINGS.save }) {
  return (
    <div className="ap-save-row">
      <Button kind="ink" type="submit" busy={save.busy} busyLabel={SETTINGS.saving}>{label}</Button>
      {save.answer && <p className={save.answer.problem ? 'ad-error' : 'ap-saved'} role={save.answer.problem ? 'alert' : 'status'}>{save.answer.text}</p>}
    </div>
  )
}

function About({ api, p, onSaved, editable }) {
  const [name, setName] = useState(p.name)
  const [tagline, setTagline] = useState(p.tagline || '')
  const [error, setError] = useState(null)
  const save = useSave()
  const cover = useSave()
  const input = useRef(null)
  const submit = e => {
    e.preventDefault()
    if (!name.trim()) { setError(SETTINGS.nameNeeded); return }
    setError(null)
    save.run(async () => onSaved(await updateProduct(api, p.id, { name: name.trim(), tagline: tagline.trim() || null })))
  }
  const pickCover = e => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    cover.run(async () => {
      const done = await uploadCover(api, p.id, file)
      onSaved({ cover_path: done?.cover_path || p.cover_path })
    })
  }
  return (
    <Card title={SETTINGS.aboutH2} aside={SETTINGS.aboutAside} id="ap-about">
      <form className="ad-form" onSubmit={submit} noValidate>
        <div className="ad-row">
          <Field label={SETTINGS.name} plain error={error}>{props => <TextInput {...props} value={name} maxLength={120} disabled={!editable} onChange={v => { save.clear(); setName(v); if (v.trim()) setError(null) }} />}</Field>
          <Field label={SETTINGS.tagline} plain>{props => <TextInput {...props} value={tagline} maxLength={120} disabled={!editable} onChange={v => { save.clear(); setTagline(v) }} />}</Field>
        </div>
        <div className="ad-row">
          <div className="ad-field">
            <span className="ap-flabel">{SETTINGS.address}</span>
            <span className="ap-readonly">{p.path ? `learn.adamslee.co.uk${p.path}` : SETTINGS.freeAddress}</span>
          </div>
          <div className="ad-field">
            <span className="ap-flabel">{SETTINGS.kind}</span>
            <span className="ap-readonly">{SETTINGS.kindLine(typeLabel(p.type), p.access_kind)}</span>
          </div>
        </div>
        {editable && (
          <div className="ad-field">
            <span className="ap-flabel">{SETTINGS.cover}</span>
            <div className="ap-cover-row">
              <Cover product={p} />
              <span className="ap-cover-words">
                <span className="ap-step-line">{p.cover_path ? SETTINGS.coverHas : SETTINGS.coverNone}</span>
                <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="ap-file" tabIndex={-1} aria-hidden="true" onChange={pickCover} />
                <Button size="sm" busy={cover.busy} onClick={() => input.current?.click()}>{p.cover_path ? SETTINGS.coverChange : SETTINGS.coverAdd}</Button>
                <span className="ad-hint">{SETTINGS.coverHint}</span>
                {cover.answer && <span className={cover.answer.problem ? 'ad-error' : 'ap-saved'} role={cover.answer.problem ? 'alert' : 'status'}>{cover.answer.problem ? cover.answer.text : SETTINGS.coverDone}</span>}
              </span>
            </div>
          </div>
        )}
        {editable && <SaveRow save={save} />}
      </form>
    </Card>
  )
}

function Terms({ api, p, extras, onSaved }) {
  const live = p.type === 'live_cohort' || p.type === 'mastermind'
  const [f, setF] = useState({
    questions: p.questions_email || '', terms: p.terms_line || '',
    months: p.min_access_months ? String(p.min_access_months) : '', from: p.min_access_from || 'purchase',
    audience: p.live_sessions_audience || 'intake', whatsapp: extras?.whatsapp_url || '', prefix: p.certificate_prefix || '',
    term: String(p.term_months || 12), termsUrl: p.settings?.terms_url || '',
  })
  // only a mastermind made in admin: The Property Accelerator's term came with its move and is shown,
  // not changed
  const ownTerm = p.type === 'mastermind' && madeInAdmin(p)
  const [errors, setErrors] = useState({})
  const save = useSave()
  const set = patch => { save.clear(); setF(s => ({ ...s, ...patch })) }
  // a new length takes the terms line with it while the line is still the length's own
  const setTerm = v => set({ term: v, ...(f.terms.trim() === termsLineFor(f.term) ? { terms: termsLineFor(v) } : {}) })
  const months = [...new Set([...MONTHS, p.min_access_months].filter(Boolean))].sort((a, b) => a - b)
  const submit = e => {
    e.preventDefault()
    const errs = {}
    if (!isEmail(f.questions)) errs.questions = SETTINGS.questionsBad
    if (p.type === 'live_cohort' && f.whatsapp.trim() && !isHttps(f.whatsapp)) errs.whatsapp = SETTINGS.whatsappBad
    if (f.prefix.trim() && !/^[A-Z]{1,6}$/.test(f.prefix.trim())) errs.prefix = SETTINGS.prefixBad
    if (f.termsUrl.trim() && !isHttps(f.termsUrl)) errs.termsUrl = SETTINGS.termsUrlBad
    setErrors(errs)
    if (Object.keys(errs).length) return
    // the settings go only when the link changed, with the rest of them as they are now
    const termsUrlChanged = f.termsUrl.trim() !== (p.settings?.terms_url || '')
    save.run(async () => {
      const row = await updateProduct(api, p.id, {
        questions_email: f.questions.trim(), terms_line: f.terms.trim() || null,
        min_access_months: f.months ? Number(f.months) : null, min_access_from: f.months ? f.from : null,
        ...(live ? { live_sessions_audience: f.audience } : {}),
        ...(ownTerm ? { term_months: Number(f.term) } : {}),
        certificate_prefix: f.prefix.trim() || null,
        ...(termsUrlChanged ? { settings: withTermsUrl(p.settings, f.termsUrl.trim()) } : {}),
      })
      const next = { product: row }
      if (p.type === 'live_cohort' && (f.whatsapp.trim() || null) !== (extras?.whatsapp_url || null)) {
        next.extras = await saveWhatsapp(api, p.id, f.whatsapp.trim() || null)
      }
      onSaved(next)
    })
  }
  return (
    <Card title={SETTINGS.termsH2} aside={SETTINGS.termsAside} id="ap-terms">
      <form className="ad-form" onSubmit={submit} noValidate>
        <Field label={SETTINGS.questions} plain hint={SETTINGS.questionsHint} error={errors.questions}>
          {props => <TextInput {...props} type="email" value={f.questions} onChange={v => set({ questions: v })} />}
        </Field>
        {p.type === 'live_cohort' && (
          <Field label={SETTINGS.whatsapp} plain hint={SETTINGS.whatsappHint} error={errors.whatsapp}>
            {props => <TextInput {...props} type="url" inputMode="url" value={f.whatsapp} onChange={v => set({ whatsapp: v })} />}
          </Field>
        )}
        <Field label={SETTINGS.termsLine} plain hint={SETTINGS.termsHint}>
          {props => <TextArea {...props} rows={2} value={f.terms} maxLength={300} onChange={v => set({ terms: v })} />}
        </Field>
        <Field label={SETTINGS.termsUrl} plain hint={SETTINGS.termsUrlHint(p.name)} error={errors.termsUrl}>
          {props => <TextInput {...props} type="url" inputMode="url" value={f.termsUrl} maxLength={300} onChange={v => set({ termsUrl: v })} />}
        </Field>
        {ownTerm
          ? (
            <Field label={SETTINGS.membershipLength} plain>
              {props => <Select {...props} value={f.term} onChange={setTerm}
                options={TERM_MONTHS.map(m => ({ value: String(m), label: SETTINGS.membershipMonths(m) }))} />}
            </Field>
          )
          : p.term_months ? <p className="ap-step-line">{SETTINGS.membership(p.term_months)}</p> : null}
        <div className="ad-row">
          <Field label={SETTINGS.minimum} plain>
            {props => <Select {...props} value={f.months} onChange={v => set({ months: v })} placeholder={SETTINGS.minimumNone}
              options={months.map(m => ({ value: String(m), label: SETTINGS.minimumMonths(m) }))} />}
          </Field>
          {f.months && (
            <div className="ad-field">
              <span className="ap-flabel" id="ap-from">{SETTINGS.from}</span>
              <Options labelledBy="ap-from" row value={f.from} onChange={v => set({ from: v })}
                options={[{ value: 'purchase', label: SETTINGS.fromPurchase }, { value: 'access_start', label: SETTINGS.fromStart }]} />
            </div>
          )}
        </div>
        {live && (
          <div className="ad-field">
            <span className="ap-flabel" id="ap-aud">{SETTINGS.audience}</span>
            <Options labelledBy="ap-aud" row value={f.audience} onChange={v => set({ audience: v })}
              options={[{ value: 'intake', label: SETTINGS.audienceIntake }, { value: 'everyone', label: SETTINGS.audienceEveryone }]} />
            <p className="ad-hint">{SETTINGS.audienceHint}</p>
          </div>
        )}
        <Field label={SETTINGS.prefix} plain hint={SETTINGS.prefixHint(f.prefix.trim())} error={errors.prefix}>
          {props => <TextInput {...props} value={f.prefix} maxLength={6} autoCapitalize="characters" onChange={v => set({ prefix: v.toUpperCase() })} className="ap-prefix" />}
        </Field>
        <SaveRow save={save} />
      </form>
    </Card>
  )
}

// The settings with the terms link set, or taken away when it's blank
function withTermsUrl(settings, url) {
  const next = { ...(settings || {}) }
  if (url) next.terms_url = url
  else delete next.terms_url
  return next
}

// A mastermind's guarantee (settings.guarantee): for owner and admin only (Adam, 18 Oct: "Members don't need to see
// the rules; it's in the terms of service"). The switch says only whether members see the on track pill under their
// attendance; no words of the guarantee reach them, so there's nothing here to type. The Property Accelerator's (from
// its move) is on; a mastermind made here has none and a copy's is off, so it starts off. Save sends the setting back
// as it is with only shown changed, so any words kept from before stay in it, unread. The numbers themselves
// (absence_limit, actions_pct, goal_days, catch_up_days and kept_since) are changed in Masterminds, Guarantee
// rules, never here: the database refuses them from this direct write (migration 20261013210000's
// guard_accountability_rules), so Save sends them back as they are and adds none (the release of 14 Oct).
function Guarantee({ api, p, onSaved }) {
  const g = p.settings?.guarantee && typeof p.settings.guarantee === 'object' ? p.settings.guarantee : null
  const [on, setOn] = useState(Boolean(g) && g.shown !== false)
  const save = useSave()
  const submit = e => {
    e.preventDefault()
    const next = { ...(g || {}), shown: on }
    save.run(async () => onSaved(await updateProduct(api, p.id, { settings: { ...(p.settings || {}), guarantee: next } })))
  }
  return (
    <Card title={SETTINGS.guaranteeH2} aside={SETTINGS.guaranteeAside} id="ap-guarantee">
      <form className="ad-form" onSubmit={submit} noValidate>
        <Switch checked={on} onChange={v => { save.clear(); setOn(v) }} label={SETTINGS.guaranteeShow}
          stateLabel={`${SETTINGS.guaranteeShow}: ${SETTINGS.guaranteeState(on)}`} />
        <p className="ap-step-line">{on ? SETTINGS.guaranteeHint() : SETTINGS.guaranteeOff}</p>
        <SaveRow save={save} />
      </form>
    </Card>
  )
}

function FreeCardWords({ api, p, onSaved }) {
  const card = p.settings?.card || {}
  const join = p.settings?.join?.route
  const [f, setF] = useState({ blurb: card.blurb || '', button: card.button_label || '', note: card.button_note || '', link: p.settings?.link_url || '' })
  const [error, setError] = useState(null)
  const save = useSave()
  const set = patch => { save.clear(); setF(s => ({ ...s, ...patch })) }
  if (p.type === 'webinar') {
    return (
      <Card title={SETTINGS.cardH2} id="ap-card-words">
        <p className="ad-lede">{SETTINGS.webinarsLine}</p>
        <p><ButtonLink to={adminPath.webinars()} size="sm">{SETTINGS.webinarsLink}</ButtonLink></p>
      </Card>
    )
  }
  const submit = e => {
    e.preventDefault()
    if (p.type === 'linked' && !join && !isHttps(f.link)) { setError(SETTINGS.linkBad); return }
    setError(null)
    const settings = { ...(p.settings || {}), card: { ...card, blurb: f.blurb.trim() || null, button_label: f.button.trim() || null, button_note: f.note.trim() || null } }
    if (p.type === 'linked' && !join) settings.link_url = f.link.trim()
    save.run(async () => onSaved(await updateProduct(api, p.id, { settings })))
  }
  return (
    <Card title={SETTINGS.cardH2} aside={SETTINGS.cardAside} id="ap-card-words">
      <form className="ad-form" onSubmit={submit} noValidate>
        <Field label={SETTINGS.blurb} plain>{props => <TextArea {...props} rows={3} maxLength={300} value={f.blurb} onChange={v => set({ blurb: v })} />}</Field>
        <div className="ad-row">
          <Field label={SETTINGS.buttonLabel} plain>{props => <TextInput {...props} maxLength={60} value={f.button} onChange={v => set({ button: v })} />}</Field>
          <Field label={SETTINGS.buttonNote} plain>{props => <TextInput {...props} maxLength={160} value={f.note} onChange={v => set({ note: v })} />}</Field>
        </div>
        {p.type === 'linked' && !join && (
          <Field label={SETTINGS.linkUrl} plain error={error}>{props => <TextInput {...props} type="url" inputMode="url" value={f.link} onChange={v => set({ link: v })} />}</Field>
        )}
        {p.type === 'linked' && join && <InfoNote>{SETTINGS.joinRoute}</InfoNote>}
        {p.type === 'resource_pack' && (
          <p className="ap-step-line">{SETTINGS.packFiles} <Link to={`${adminPath.resources()}?product=${p.slug}`}>{SETTINGS.packLink}</Link></p>
        )}
        <SaveRow save={save} />
      </form>
    </Card>
  )
}

function LookCard({ api, p, onSaved }) {
  const own = p.own_look || {}
  const [f, setF] = useState({ look: p.look, ownBg: own.bg || '#FFFFFF', ownAccent: own.accent || '#0D0D0D', ownText: own.text || '#0D0D0D', font: fontFor(own.heading_font).key })
  const [error, setError] = useState(null)
  const save = useSave()
  const draft = p.state === 'draft'
  if (!draft) {
    return (
      <Card title={SETTINGS.lookH2} aside={SETTINGS.lookAside} id="ap-look">
        <p className="ad-lede">{SETTINGS.lookLive(LOOK_NAMES[p.look]?.[0] || p.look)}</p>
      </Card>
    )
  }
  const submit = e => {
    e.preventDefault()
    if (f.look === 'own' && ![f.ownBg, f.ownAccent, f.ownText].every(isHex)) { setError('Each colour needs a 6-digit code, like #0D0D0D.'); return }
    setError(null)
    const patch = { look: f.look }
    if (f.look === 'own') {
      const look = { bg: normalHex(f.ownBg), accent: normalHex(f.ownAccent), text: normalHex(f.ownText), heading_font: f.font }
      patch.own_look = look
      patch.own_look_contrast_ok = contrastCheck(look).pass
    }
    save.run(async () => onSaved(await updateProduct(api, p.id, patch)))
  }
  return (
    <Card title={SETTINGS.lookH2} aside={SETTINGS.lookAside} id="ap-look">
      <form className="ad-form" onSubmit={submit} noValidate>
        <LookPicker value={f} onChange={patch => { save.clear(); setF(s => ({ ...s, ...patch })) }}
          looks={looksFor(p.type)} fixed={ADD.lookFixed[p.type] || null} />
        {error && <p className="ad-error" role="alert">{error}</p>}
        <SaveRow save={save} />
      </form>
    </Card>
  )
}

function RecordingLine({ d }) {
  return (
    <Card title={SETTINGS.recordingH2} id="ap-recording">
      <p className="ad-lede">{d.recording ? SETTINGS.recordingLine(d.recording.title, d.length ? duration(d.length) : 'no length yet') : SETTINGS.recordingNone}</p>
      <p><ButtonLink to={adminPath.recordings()} size="sm">{SETTINGS.recordingLink}</ButtonLink></p>
    </Card>
  )
}

// How the hub runs it: shown, not changed, except a product made in admin, whose Stripe purchases an owner
// makes live here, asked first (a paid product that isn't a private client space, while it's in shadow)
function Runs({ api, p, billing, owner, onBilling }) {
  const moved = isMovedIn(p)
  const made = madeInAdmin(p)
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(false)
  const client = p.type === 'client_space'
  const canMakeLive = owner && moved && made && !client && billing && billing.mode !== 'live'
  // a product made here that's paid monthly waits for the plan's step 8.4 (model.js paidMonthly)
  const monthly = made && !client && billing?.mode !== 'live' && paidMonthly(p)
  async function makeLive() {
    setBusy(true)
    setError(null)
    try {
      const row = await setPurchasesLive(api, p.id)
      onBilling(row)
      setAsking(false)
      setDone(true)
    } catch (e) { setError(e) } finally { setBusy(false) }
  }
  const rows = [
    moved && billing ? [SETTINGS.mode, billing.mode === 'live' ? SETTINGS.modeLive : SETTINGS.modeShadow] : null,
    moved && billing ? [SETTINGS.accessEmail, billing.access_email_on ? SETTINGS.accessEmailOn : SETTINGS.accessEmailOff] : null,
    [SETTINGS.where, moved ? SETTINGS.whereIn : SETTINGS.whereOut(outsideHost(p))],
  ].filter(Boolean)
  return (
    <Card title={SETTINGS.runsH2} aside={made ? (client ? SETTINGS.runsAsideClient : SETTINGS.runsAsideNew) : SETTINGS.runsAside} id="ap-runs">
      <dl className="ap-facts">
        {rows.map(([k, v]) => <div className="ap-fact" key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
      </dl>
      {canMakeLive && !asking && <p><Button size="sm" onClick={() => { setAsking(true); setDone(false) }}>{SETTINGS.makeLive}</Button></p>}
      {canMakeLive && asking && (
        <ConfirmPanel text={SETTINGS.makeLiveConfirm(p.name)} confirmLabel={SETTINGS.makeLiveYes} cancelLabel={SETTINGS.makeLiveNo}
          busy={busy} error={error} onConfirm={makeLive} onCancel={() => { setAsking(false); setError(null) }} />
      )}
      {done && <p className="ap-saved" role="status">{SETTINGS.madeLive}</p>}
      <p className="ad-hint">{!made ? SETTINGS.runsNote : client ? SETTINGS.runsNoteClient : monthly ? SETTINGS.runsNoteMonthly : SETTINGS.runsNoteNew}</p>
    </Card>
  )
}
